/*
PURPOSE: Editorial public-artist landing page — the per-artist viewer destination.
Section-per-concept composition that mirrors Home's architecture:
  1. Hero            (ivory)  — avatar + name + subtitle + how-it-works line
                                + platform-comparison tooltip + creator card
  2. Why Coral       (cream)  — shared WhyCoralSection
  3. Your Impact     (ivory)  — heading + 3-bullet benefits
  4. Artist Ecosystem(cream)  — 2-col grid: framing + EcosystemVitals card
  5. Our Vision      (ivory)  — eyebrow + heading + EcosystemBloom
  6. Small Acts      (cream)  — EditorialImpactCalculator + back CTA
  7. Straight Answers(ivory)  — FAQ accordion + back CTA
Every copy key is consumed verbatim from useCopy().publicPages.publicArtist.*;
no fabricated copy ships.
USE CASES:
- Coral viewers landing on /a/:id (an artist's public-facing page). The route
  uses :id as the artistUrl param.
DOMAIN BOUNDARIES:
- Thin composition of editorial primitives plus shared AllInOneCoralCreator
  (editorial + compact props), FAQItem, EcosystemVitals, EcosystemBloom, and
  EditorialImpactCalculator. Functional contracts preserved verbatim:
  getArtistDetailsPublic fetch, PUBLIC_ARTIST_VIEW analytics, storeReferralUrl.
- Two creator-card instances render: desktop (hidden lg:block, hero right col)
  and mobile (block lg:hidden, inline below hero grid). One is visible per
  viewport; CtaPill scroll lands at the hero wrapper, which contains both.
*/

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useCopy } from '../../../hooks/useCopy';
import { faqData } from '../../../copy/faqData';
import Spinner from '../../../components/Spinner/Spinner';
import { storeReferralUrl } from '../../../utils/referralUtils';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import { BlendedArtistListItem } from 'shared/types/platformTypes';
import { getArtistDetailsPublic } from '../../../api/api';
import AllInOneCoralCreator from '../../../components/AllInOneCoralCreator/AllInOneCoralCreator';
import FAQItem from '../../../components/FAQItem/faqItem';
import EcosystemVitals from '../../../components/EcosystemVitals/EcosystemVitals';
import EcosystemBloom from '../../../components/EcosystemBloom/EcosystemBloom';
import HexBloom from '../../../components/HexBloom/HexBloom';
import EditorialImpactCalculator from '../Home/EditorialImpactCalculator';
import { Tooltip } from 'react-tooltip';
import MetaHead from '../../../components/MetaHead/MetaHead';
import PublicPageErrorBoundary from '../../../components/PublicPageErrorBoundary/PublicPageErrorBoundary';
import {
  EditorialSection,
  Eyebrow,
  DisplayHeading,
  BodyProse,
  CtaPill,
  WhyCoralSection,
} from '../../../components/editorial';

/** FNV-1a: stable numeric seed from a string (same algorithm as HexBloom's
 *  internal hashStringToSeed, reproduced here because it is not exported). */
function hashStringToSeed(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

const PublicArtistLanding: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const copy = useCopy();
  const [artistData, setArtistData] = useState<BlendedArtistListItem | null>(null);
  // Distinguishes "still loading" (artistData null, notFound false) from
  // "resolved to not-found" (artistData null, notFound true) — a fetch that
  // resolves to an empty array or throws must land here, not spin forever.
  const [notFound, setNotFound] = useState(false);

  // Ref for the creator card wrapper; CTAs scroll here.
  const coralCreatorRef = useRef<HTMLDivElement>(null);

  // Lifecycle-hygienic fetch: AbortController wired to effect cleanup so an
  // unmount within the fetch window silently aborts instead of calling setState
  // on an unmounted component. AbortError is swallowed; all other errors are
  // logged (pre-existing behaviour).
  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();
    let cancelled = false;

    const referralUrl = `${window.location.origin}${location.pathname}`;
    storeReferralUrl(referralUrl);

    const fetchArtistData = async (artistUrl: string) => {
      try {
        // getArtistDetailsPublic does not accept AbortSignal; the cancelled
        // flag guards setState if the component unmounts before the fetch
        // resolves (the AbortController is still created so future callers
        // that do accept signal can be wired in without structure changes).
        const data = await getArtistDetailsPublic(artistUrl, undefined, undefined, true);
        if (cancelled) return;
        if (data && data.length > 0) {
          setArtistData(data[0]);
          trackEvent(EventName.PUBLIC_ARTIST_VIEW, { artistUrl, artistName: data[0].artistName! });
        } else {
          setNotFound(true);
        }
      } catch (error) {
        if (cancelled) return;
        if ((error as Error).name === 'AbortError') return;
        console.error('Error fetching artist data:', error);
        setNotFound(true);
      }
    };

    fetchArtistData(id);

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.pathname]);

  // Scroll to the coral creator card. Timer fire-and-forget on click; the
  // ref check inside the callback guards against unmount during the 100ms
  // window (T3 pattern: no cleanup needed for a fire-and-forget onClick timer).
  const scrollToCoralCreator = useCallback(() => {
    setTimeout(() => {
      const el = coralCreatorRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  // Resolved to not-found (empty response or fetch error) — genuine removal:
  // render the in-page not-found without leaking the artist's identity via
  // MetaHead (generic default title/description; no artistName is read here).
  if (notFound) {
    return (
      <>
        <MetaHead noindex />
        <PublicPageErrorBoundary>
          <div className="flex flex-col min-h-screen bg-ivory">
            <EditorialSection background="ivory" topRule={false}>
              <DisplayHeading as="h1">{copy.publicPages.publicArtist.notFound.title}</DisplayHeading>
              <BodyProse className="mt-6">
                <p>{copy.publicPages.publicArtist.notFound.message}</p>
              </BodyProse>
              <div className="mt-10">
                <CtaPill href="/">{copy.publicPages.publicArtist.notFound.cta}</CtaPill>
              </div>
            </EditorialSection>
          </div>
        </PublicPageErrorBoundary>
      </>
    );
  }

  // Still loading — fetch not yet resolved either way.
  if (!artistData) {
    return (
      <div className="flex items-center justify-center h-screen bg-ivory">
        <Spinner />
      </div>
    );
  }

  const artistName = artistData.artistName || '';
  // Deterministic per-artist bloom seed derived from the artist URL.
  const bloomSeed = id ? hashStringToSeed(id) : 4;

  // Reused chrome for both desktop + mobile creator-card instances.
  const creatorCardChrome =
    'bg-white rounded-3xl shadow-[0_24px_48px_-24px_rgba(12,19,45,0.18)] border border-editorial-muted/5';

  // A refused artist never reaches this render (the fetch resolves to
  // not-found above), so the backing card is always the live creator card.
  const backingCard = (
    <div className={creatorCardChrome}>
      <AllInOneCoralCreator artistId={artistData.artistId} editorial compact />
    </div>
  );

  return (
    <>
      <MetaHead
        title={copy.publicPages.publicArtist.meta.title(artistName || 'Artist')}
        description={copy.publicPages.publicArtist.meta.description(artistName || 'this artist')}
      />
      <PublicPageErrorBoundary>
        <div className="flex flex-col min-h-screen bg-ivory">

          {/* ── Hero ── slim editorial section. Left: avatar + name + subtitle.
               Right (lg+): creator card. Below grid (<lg): mobile creator card
               inline. coralCreatorRef wraps the section so CTA scroll lands
               at the top of Hero (where the visible creator card is). */}
          <div ref={coralCreatorRef}>
            <EditorialSection background="ivory" topRule={false}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">

                {/* Left column — identity only */}
                <div className="lg:col-span-7 relative">
                  {/* Decorative per-artist bloom behind the headline — NOT a data
                      claim. aria-hidden; purely ornamental. Seed derived from the
                      artistUrl via FNV-1a so it is stable and unique per artist. */}
                  <div
                    className="absolute top-0 right-0 lg:-right-8 pointer-events-none select-none opacity-60 -z-0"
                    aria-hidden="true"
                  >
                    <HexBloom seed={bloomSeed} size={360} />
                  </div>

                  {/* Artist avatar — stacked above the name. Soft editorial
                      treatment (shadow + thin ring; no bold border). Conditional
                      on imageUrl. */}
                  {artistData.artistData?.imageUrl && (
                    <img
                      src={artistData.artistData.imageUrl}
                      alt={artistName}
                      className="relative z-10 w-28 h-28 md:w-32 md:h-32 rounded-full object-cover shadow-[0_8px_24px_-8px_rgba(12,19,45,0.18)] ring-1 ring-editorial-muted/10 mb-5"
                    />
                  )}

                  {/* Artist name — h1, Cormorant, editorial palette */}
                  <h1
                    className="relative z-10 font-cormorant font-normal text-editorial-muted text-[40px] leading-[1.04] md:text-[56px] md:leading-[1.02]"
                    style={{ letterSpacing: '-0.01em' }}
                  >
                    {artistName}
                  </h1>

                  {/* Subtitle — action + mechanic paired as one lead paragraph.
                      Inter bumped to fill the hero space below the name. */}
                  <p className="relative z-10 mt-6 text-[17px] md:text-[19px] text-editorial-muted leading-[1.55] max-w-prose">
                    {copy.publicPages.publicArtist.hero.subtitle(artistName)}
                  </p>

                  {/* Community — closing standalone framing line, paragraph-
                      spaced from the lead so it lands as its own thought. */}
                  <p className="relative z-10 mt-7 text-[17px] md:text-[19px] text-editorial-muted/85 leading-[1.55] max-w-prose">
                    {copy.publicPages.publicArtist.hero.community}
                  </p>

                  {/* Platform comparison tooltip — keyboard-accessible <button>
                      trigger. Hoisted into the hero so the fees claim sits with
                      the framing. */}
                  <div className="relative z-10 mt-6 text-sm text-editorial-muted/70">
                    <button
                      type="button"
                      className="inline text-left cursor-help focus:outline-none focus-visible:underline"
                      data-tooltip-id="compareTooltip"
                      data-tooltip-html={copy.publicPages.publicArtist.hero.platformComparison.tooltipHtml}
                    >
                      {copy.publicPages.publicArtist.hero.platformComparison.text}{' '}
                      <span className="underline text-coral-orange">
                        {copy.publicPages.publicArtist.hero.platformComparison.linkText}
                      </span>
                    </button>
                    <Tooltip
                      id="compareTooltip"
                      className="z-50 !max-w-[min(90vw,28rem)]"
                    />
                  </div>
                </div>

                {/* Right column — desktop creator card (lg+ only) */}
                <div className="hidden lg:block lg:col-span-5">
                  {backingCard}
                </div>
              </div>

              {/* Mobile creator card — inline below the hero grid, only <lg.
                  Action-forward: gives mobile users an immediate CTA without
                  scrolling past framing. */}
              <div className="block lg:hidden mt-10">
                {backingCard}
              </div>
            </EditorialSection>
          </div>

          {/* ── Why Coral Exists ── shared editorial section, mirrors Home */}
          <WhyCoralSection />

          {/* ── Your Impact ── eyebrow + heading + 3-bullet benefits.
               Mechanic explanation + fees link moved to the hero; this
               section frames what backing actually achieves. */}
          <EditorialSection background="ivory" id="your-impact">
            <div className="max-w-3xl">
              <Eyebrow>{copy.publicPages.publicArtist.yourImpact.sectionTitle}</Eyebrow>
              <DisplayHeading as="h2" scale="heading" className="mt-8">
                {copy.publicPages.publicArtist.yourImpact.heading}
              </DisplayHeading>

              {/* Benefits list — the "three things" the heading promises. */}
              <ul className="mt-8 space-y-3">
                <li className="flex items-start gap-2 text-[15px] text-editorial-muted/80 leading-[1.6]">
                  <span className="text-coral-orange mt-1" aria-hidden="true">•</span>
                  {copy.publicPages.publicArtist.yourImpact.benefits.first}
                </li>
                <li className="flex items-start gap-2 text-[15px] text-editorial-muted/80 leading-[1.6]">
                  <span className="text-coral-orange mt-1" aria-hidden="true">•</span>
                  {copy.publicPages.publicArtist.yourImpact.benefits.second}
                </li>
                <li className="flex items-start gap-2 text-[15px] text-editorial-muted/80 leading-[1.6]">
                  <span className="text-coral-orange mt-1" aria-hidden="true">•</span>
                  {copy.publicPages.publicArtist.yourImpact.benefits.third(artistName || 'this artist')}
                </li>
              </ul>
            </div>
          </EditorialSection>

          {/* ── Artist Ecosystem ── eyebrow + heading + body framing the data,
               with EcosystemVitals card to the right. */}
          <EditorialSection background="cream" id="artist-ecosystem">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              <div className="lg:col-span-5">
                <Eyebrow>{copy.publicPages.publicArtist.ecosystem.sectionTitle}</Eyebrow>
                <DisplayHeading as="h2" scale="heading" className="mt-8" measure={false}>
                  {copy.publicPages.publicArtist.ecosystem.heading(artistName)}
                </DisplayHeading>
                <BodyProse className="mt-6">
                  {copy.publicPages.publicArtist.ecosystem.body}
                </BodyProse>
              </div>
              <div className="lg:col-span-7">
                <EcosystemVitals artistName={artistName} />
              </div>
            </div>
          </EditorialSection>

          {/* ── Vision ── OUR VISION eyebrow + Cormorant h2 + EcosystemBloom */}
          <EditorialSection background="ivory" id="vision">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
              <div>
                <Eyebrow>{copy.publicPages.publicArtist.vision.sectionTitle}</Eyebrow>
                <DisplayHeading as="h2" scale="heading" className="mt-8" measure={false}>
                  {copy.publicPages.publicArtist.vision.description}
                </DisplayHeading>
              </div>
              <div className="flex justify-center md:justify-end">
                <EcosystemBloom />
              </div>
            </div>
          </EditorialSection>

          {/* ── Small Acts, Real Impact ── impact calculator + back-artist CTA */}
          <EditorialSection background="cream" id="coral-dynamics">
            <Eyebrow>{copy.publicPages.publicArtist.coralDynamics.sectionTitle}</Eyebrow>
            <div className="mt-8">
              <EditorialImpactCalculator />
            </div>
            <div className="flex justify-center mt-12">
              <CtaPill onClick={scrollToCoralCreator}>
                {copy.publicPages.publicArtist.cta.buildCoral(artistName)}
              </CtaPill>
            </div>
          </EditorialSection>

          {/* ── Straight Answers (FAQ) ── 2-col editorial grid; scoped override
               re-themes FAQItem for the surface without touching the shared
               component. faqDescription serves as the section h2 (Home pattern;
               no redundant "FAQs" subheading). */}
          <EditorialSection background="ivory" id="faq">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
              <div>
                <Eyebrow>{copy.publicPages.publicArtist.support.sectionTitle}</Eyebrow>
                <DisplayHeading as="h2" scale="heading" className="mt-8">
                  {copy.publicPages.publicArtist.support.faqDescription(artistName)}
                </DisplayHeading>
              </div>
              <div className="space-y-2 [&_button_span]:!text-editorial-ink [&_button+div]:!text-editorial-muted/80">
                {faqData.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
            <div className="flex justify-center mt-12">
              <CtaPill onClick={scrollToCoralCreator}>
                {copy.publicPages.publicArtist.cta.startCoral(artistName)}
              </CtaPill>
            </div>
          </EditorialSection>

        </div>
      </PublicPageErrorBoundary>
    </>
  );
};

export default PublicArtistLanding;
